gsap.registerPlugin(ScrollTrigger);

(function belowFoldFX(){
  /* ---------------- helpers ---------------- */
  const PIN_SELECTOR = "#vision-steps .vf-pin"; // adjust if your pin wrapper differs

  // Build once, even if called multiple times (Theme Editor, PageFly, etc.)
  function markReady(el){ el.dataset.gsapReady = "1"; }
  function isReady(el){ return el?.dataset?.gsapReady === "1"; }

  // Common ST options: pin-aware + recalculates after layout changes
  const stBase = {
    start: "top bottom",
    end:   "bottom bottom",
    scrub: 3,
    anticipatePin: 1,
    invalidateOnRefresh: true,
    refreshPriority: -10 // ensure these measure after the pin
  };

  /* ---------------- text (SplitType optional) ---------------- */
  function initFadeText(root=document){
    root.querySelectorAll(".fade-text").forEach((el)=>{
      if (isReady(el)) return;

      // Optional SplitType
      let chars = el.querySelectorAll(".char");
      if (!chars.length && window.SplitType){
        const split = new SplitType(el, { types: "words, chars" });
        chars = split.chars || el.querySelectorAll(".char");
      }
      if (!chars.length) return;

      gsap.set(chars, { autoAlpha: 0.1 });

      gsap.timeline({
        scrollTrigger: {
          ...stBase,
          trigger: el
        }
      }).to(chars, {
        autoAlpha: 1,
        ease: "none",
        stagger: { from: "start", amount: 60 },
        immediateRender: false // prevent “born progressed” flashes
      });

      markReady(el);
    });
  }

  /* ---------------- buttons (label rebuild) ---------------- */
  function initFadeTextButton(root=document){
    root.querySelectorAll(".fade-text-button").forEach((el)=>{
      if (isReady(el)) return;

      const icon = el.querySelector(":scope > svg") || el.querySelector("svg") || null;

      // capture text
      const existingLabel = el.querySelector(":scope > .label");
      const nonSvgChild   = Array.from(el.children).find(c => c !== icon && !c.classList.contains("label"));
      let text = existingLabel
        ? existingLabel.textContent
        : nonSvgChild
        ? nonSvgChild.textContent
        : Array.from(el.childNodes)
            .filter(n => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length)
            .map(n => n.textContent)
            .join("");
      text = (text || "").replace(/\s+/g, " ").trim();
      if (!text) return;

      // rebuild label
      const label = document.createElement("div");
      label.className = "label";
      for (const ch of text){
        const s = document.createElement("span");
        s.className = "char";
        s.textContent = ch === " " ? "\u00A0" : ch;
        label.appendChild(s);
      }
      el.innerHTML = "";
      el.appendChild(label);
      if (icon){
        icon.classList.add("arrow");
        icon.style.transformOrigin = "center";
        el.appendChild(icon);
      }

      const targets = icon ? [...label.querySelectorAll(".char"), icon] : [...label.querySelectorAll(".char")];
      gsap.set(targets, { autoAlpha: 0.1 });

      gsap.timeline({
        scrollTrigger: {
          ...stBase,
          trigger: el
        }
      }).to(targets, {
        autoAlpha: 1,
        ease: "none",
        stagger: { from: "start", amount: 60 },
        immediateRender: false
      });

      markReady(el);
    });
  }

  /* ---------------- images (clip reveal) ---------------- */
  function initFadeImage(root=document){
    const setups = [
      { selector: ".fade-image-top",  fromClip: 'inset(0% 0% 100% 0%)' }, // hide bottom
      { selector: ".fade-image-left", fromClip: 'inset(0% 100% 0% 0%)' }  // hide right
    ];
    setups.forEach(({selector, fromClip})=>{
      root.querySelectorAll(selector).forEach((el)=>{
        if (isReady(el)) return;

        gsap.fromTo(
          el,
          { clipPath: fromClip, autoAlpha: 0.1, immediateRender: false },
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            autoAlpha: 1,
            ease: "none",
            scrollTrigger: {
              ...stBase,
              trigger: el
            }
          }
        );

        markReady(el);
      });
    });
  }

  /* ---------------- wire everything, pin-aware ---------------- */
  function wire(root=document){
    initFadeText(root);
    initFadeTextButton(root);
    initFadeImage(root);
  }

  // Ensure our fades measure AFTER the pin is in place:
  // 1) wait a tick,
  // 2) sort by refreshPriority,
  // 3) refresh twice (fonts/layout & pin spacing).
  function deferredRefresh(){
    requestAnimationFrame(() => {
      ScrollTrigger.sort(true);
      ScrollTrigger.refresh();
      setTimeout(() => ScrollTrigger.refresh(), 60);
    });
  }

  /* ---------------- boot ---------------- */
  function boot(root=document){
    // If the pin element exists, give its ST (if created later) priority.
    // Easiest guarantee: wire our fades slightly later.
    if (document.readyState === "loading"){
      document.addEventListener("DOMContentLoaded", () => { wire(root); deferredRefresh(); });
    } else {
      wire(root);
      deferredRefresh();
    }
  }

  // Initial boot
  boot();

  // Shopify Theme Editor / PageFly dynamic mounts
  document.addEventListener("shopify:section:load",   (e) => { wire(e.target); deferredRefresh(); });
  document.addEventListener("shopify:section:select", (e) => { wire(e.target); deferredRefresh(); });

  // Watch late inserts
  new MutationObserver((muts)=>{
    let rerun = false;
    for (const m of muts){
      for (const n of m.addedNodes){
        if (n.nodeType !== 1) continue;
        if (n.matches?.(".fade-text, .fade-text-button, .fade-image-top, .fade-image-left") ||
            n.querySelector?.(".fade-text, .fade-text-button, .fade-image-top, .fade-image-left")){
          rerun = true;
        }
      }
    }
    if (rerun){ wire(document); deferredRefresh(); }
  }).observe(document.body, { childList:true, subtree:true });

  // Extra safety after webfonts
  (document.fonts?.ready || Promise.resolve()).then(()=> deferredRefresh());

  // If your Visions pin ST is created here/elsewhere, give it top priority:
  // When you create it, add: { refreshPriority: 1000 } to its ScrollTrigger.
  // Example:
  // ScrollTrigger.create({ trigger: PIN_SELECTOR, pin:true, start:"top top", end:"+=7200", refreshPriority:1000 });
})();


// ----------------------------------------------------
// requires gsap + ScrollTrigger (and optionally SplitType) loaded before this
// <script src="https://cdn.jsdelivr.net/npm/gsap@3/dist/gsap.min.js"></script>
// <script src="https://cdn.jsdelivr.net/npm/gsap@3/dist/ScrollTrigger.min.js"></script>

gsap.registerPlugin(ScrollTrigger);

/* ------------------ FADE TEXT (SplitType) ------------------ */
function initFadeText(root = document) {
  root.querySelectorAll(".fade-text").forEach((el) => {
    if (el.dataset.gsapReady) return;

    // SplitType may be absent; skip if not available
    if (window.SplitType) {
      el._split = new SplitType(el, { types: "chars" });
    }

    const isParagraph = el.classList.contains("is-paragraph");
    const prev = el.previousElementSibling;
    const delayStart = isParagraph && prev && prev.classList.contains("is-heading");

    gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top bottom",
        end: "bottom bottom",
        scrub: 3
      }
    }).fromTo(
      el.querySelectorAll(".char"),
      { autoAlpha: 0.1 },
      {
        autoAlpha: 1,
        ease: "none",
        stagger: { from: "start", amount: 60 }
      },
      delayStart ? "+=0.5" : 0
    );

    el.dataset.gsapReady = "1";
  });
}

/* ------------------ FADE TEXT BUTTON ------------------ */
function initFadeTextButton(root = document) {
  root.querySelectorAll(".fade-text-button").forEach((el) => {
    if (el.dataset.gsapReady) return;

    const icon = el.querySelector(":scope > svg") || el.querySelector("svg") || null;

    // read text (existing .label > any non-SVG child > raw text)
    const existingLabel = el.querySelector(":scope > .label");
    const nonSvgChild   = Array.from(el.children).find(c => c !== icon && !c.classList.contains("label"));
    let text = existingLabel
      ? existingLabel.textContent
      : nonSvgChild
      ? nonSvgChild.textContent
      : Array.from(el.childNodes)
          .filter(n => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length)
          .map(n => n.textContent)
          .join("");
    text = (text || "").replace(/\s+/g, " ").trim();
    if (!text) return;

    // rebuild: <div class="label"><span class="char">…</span></div> [svg]
    const label = document.createElement("div");
    label.className = "label";
    for (const ch of text) {
      const s = document.createElement("span");
      s.className = "char";
      s.textContent = ch === " " ? "\u00A0" : ch;
      label.appendChild(s);
    }
    el.innerHTML = "";
    el.appendChild(label);
    if (icon) {
      icon.classList.add("arrow");
      icon.style.removeProperty("transform");
      icon.style.transformOrigin = "center";
      el.appendChild(icon);
    }

    const chars   = label.querySelectorAll(".char");
    const targets = icon ? [...chars, icon] : [...chars];

    gsap.set(targets, { autoAlpha: 0.1 });

    gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top bottom",
        end:   "bottom bottom",
        scrub: 3,
        invalidateOnRefresh: true
      }
    })
    .to(targets, {
      autoAlpha: 1,
      ease: "none",
      stagger: { from: "start", amount: 60 }
    });

    requestAnimationFrame(() => ScrollTrigger.refresh());
    el.dataset.gsapReady = "1";
  });
}

/* ------------------ FADE IMAGE ------------------ */
function initFadeImage(root = document) {
  const imageSelectors = [
    { selector: ".fade-image-top",  direction: "top"  },
    { selector: ".fade-image-left", direction: "left" }
  ];

  imageSelectors.forEach(({ selector, direction }) => {
    root.querySelectorAll(selector).forEach((el) => {
      if (el.dataset.gsapReady) return;

      const prev = el.previousElementSibling;
      const delayStart = prev && prev.classList.contains("is-heading");

      let fromClip, toClip;
      if (direction === "top")  { fromClip = 'inset(0% 0% 100% 0%)'; toClip = 'inset(0% 0% 0% 0%)'; }
      if (direction === "left") { fromClip = 'inset(0% 100% 0% 0%)'; toClip = 'inset(0% 0% 0% 0%)'; }

      gsap.fromTo(
        el,
        { clipPath: fromClip, autoAlpha: 0.1 },
        {
          clipPath: toClip, autoAlpha: 1, ease: "none",
          scrollTrigger: { trigger: el, start: "top bottom", end: "bottom bottom", scrub: 3 }
        },
        delayStart ? '+=0.5' : 0
      );

      el.dataset.gsapReady = "1";
    });
  });
}

/* -------------------- BOOTSTRAP/RERUN ---------------------- */
function runAll(root = document) {
  initFadeText(root);
  initFadeTextButton(root);
  initFadeImage(root);
  (document.fonts?.ready || Promise.resolve()).then(() => ScrollTrigger.refresh());
  setTimeout(() => ScrollTrigger.refresh(), 50);
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => runAll());
} else {
  runAll();
}
window.addEventListener("load", () => ScrollTrigger.refresh());
document.addEventListener("shopify:section:load",   (e) => runAll(e.target));
document.addEventListener("shopify:section:select", (e) => runAll(e.target));
new MutationObserver((muts) => {
  let rerun = false;
  for (const m of muts) {
    m.addedNodes.forEach((n) => {
      if (n.nodeType === 1 && (n.matches?.(".fade-text, .fade-text-button, .fade-image-top, .fade-image-left") ||
          n.querySelector?.(".fade-text, .fade-text-button, .fade-image-top, .fade-image-left"))) {
        rerun = true;
      }
    });
  }
  if (rerun) runAll();
}).observe(document.body, { childList: true, subtree: true });

